import { z } from 'zod';
import { StripeNormalizedPayoutMetadata } from './payout';
import { StripeTransferMetadata } from './stripeMetadata';

/**
 * See: https://stripe.com/docs/api/sources/object
 */
export type EStripeSource = {
  object: 'source';
  id: string;
  name: string;
  exp_month: string;
  exp_year: string;
  last4: string;
  bank_name?: string;

  /**
   * Only chargeable sources can be used to create a charge.
   */
  status: 'canceled' | 'chargeable' | 'consumed' | 'failed' | 'pending';

  /**
   * Other types are possible but we don't use them in our app.
   */
  type:
    | 'ach_credit_transfer'
    | 'ach_debit'
    | 'card'
    | 'card_present'
    | 'us_bank_account'
    | unknown;

  account_holder_type: string;
  routing_number?: string;
  account_holder_name: string;
  brand: string;
};

/**
 * Stripe says this record may not be accurate and is parsed data from the check, but
 * it's the best way to get the amount of the check that was utilized, which is not data
 * that's parsed from the check.
 */
export type StripeCheckReference = {
  /**
   * e.g. 'paper_check_parsed_invoice'  Not sure what else this might be...
   */
  object: string;

  /**
   * Amount in cents of the check that was utilized to reconcile
   */
  amount: number;

  /**
   * Currency of the check that was utilized to reconcile e.g. 'usd'
   */
  currency: string;

  /**
   * Date in seconds the invoice was issued
   * Not certain if that's entirely accurate information (source: Stripe API docs)
   */
  date: number;

  /**
   * Usually the `invoice_number` that the check was reconciled against
   * i.e. (not stripe invoice id)
   */
  text: string;
};

export type StripeCheckReconciliationRecord = {
  /**
   * Amount of invoice that the check was reconciled against
   * WARNING: This is NOT the amount that was reconciled!
   */
  amount: number;

  /**
   * Currency of the check (e.g. "usd")
   */
  currency: string;

  /**
   * Customer cu_ id associated with the check
   * NOTE: this is a different 'cu_' prefixed id, not the standard 'cus_' prefixed id we record
   */
  customer: string;

  /**
   * Stripe invoice id that the check was reconciled against
   */
  invoice: string;
};

export type EStripePaperCheck = {
  amount: number;
  currency: 'usd';
  customer: string | null;

  /**
   * Stripe check id i.e. pchk_*
   */
  id: string;
  object: 'paper_check';
  printed_details: {
    bank_account_number_last_4: string;
    bank_routing_number: string;
    check_issued_at: number;
    number_on_check: string;
    payee: string;
    payer: string;
  };
  reconciled_at?: number;
  reconciled_by?: 'auto' | string;

  /**
   * Only available when directly retrieving the check from Stripe
   * Do not use this amount for reconciliation, use the references instead
   */
  reconciliation_records?: StripeCheckReconciliationRecord[];

  /**
   * Only available when directly retrieving the check from Stripe
   * This has the most accurate amount of the check that was reconciled
   */
  references?: StripeCheckReference[];

  status: 'reconciled' | 'settled' | 'bounced' | string;
  status_transitions: {
    bounced_at: number | null;
    deposited_at: number;
    expected_funds_available_at: number;
    funds_available_at: number | null;
  };
  bounce_reason?: string | null;
  payment_intent?: string | null;
};

/**
 * See: https://stripe.com/docs/api/customer_bank_accounts/object
 */
export type EStripeBankAccount = {
  id: string;
  object: 'bank_account';
  account_holder_name: string;
  account_holder_type: string;
  bank_name: string;
  fingerprint: string;
  last4: string;
  routing_number: string;
  status: 'new' | 'validated' | 'verified' | 'verification_failed' | 'errored';
};

// Stripe returns more fields than these types declare, some of them nullable,
// so the API response schemas check identity only and pass the object through.
const StripeBankAccountIdentitySchema = z.object({
  id: z.string(),
  object: z.literal('bank_account')
});
export const StripeBankAccountSchema = z.custom<EStripeBankAccount>(
  data => StripeBankAccountIdentitySchema.safeParse(data).success
);

export type StripeInvoiceCustomField = {
  name: string;
  value: string;
};

export type EStripeChargeStatus = 'pending' | 'succeeded' | 'failed';

export type StripePaymentMethodType =
  | 'card'
  | 'ach_credit_transfer'
  | 'paper_check'
  | 'us_bank_account';

/**
 * https://stripe.com/docs/api/payment_intents/object
 */
export type EStripePaymentIntent = {
  id: string;
  amount: number;
  currency: 'usd';
  customer?: string; // ID or expandable. Add expanded type here when needed
  payment_method?: string; // ID or expandable. Add expanded type here when needed
  payment_method_types: StripePaymentMethodType[];
  off_session: boolean;
  setup_future_usage: 'off_session' | 'on_session' | undefined;
  metadata: Record<string, any>;
  status:
    | 'requires_payment_method'
    | 'requires_confirmation'
    | 'requires_action'
    | 'processing'
    | 'requires_capture'
    | 'canceled'
    | 'succeeded';
};

/**
 * See: https://stripe.com/docs/api/customers/object
 */
export type EStripeCustomer = {
  id: string;
  name: string;
};

export type EStripeConfirmation = {
  deleted: boolean;
};

/**
 * See: https://stripe.com/docs/api/balance_transactions/object
 */
export type EStripeBalanceTransaction = {
  /**
   * The 'source' is expandable, so it is only present when asked for.
   * This can be one of multiple object types.
   *
   * See: https://stripe.com/docs/api/balance_transactions/object#balance_transaction_object-source
   */
  source?: {
    id: string;
    refunded?: boolean;
    metadata: {
      userNoticeId?: string;
      type?: string;
    };
    created: number;

    /**
     * The 'source_transfer' is expandable, so it is only present when asked for.
     * This is only present on Stripe Connect charges.
     *
     * See: https://stripe.com/docs/api/charges/object#charge_object-source_transfer
     */
    source_transfer?: {
      id: string;
      metadata: {
        userNoticeId?: string;
        type?: string;
        newspaperOrderId?: string;
        adType?: string;
      };
    };

    /**
     * The 'transfer_reversal' is expandable, so it is only present when asked for.
     * This is only present on Stripe Connect refunds.
     *
     * See: https://stripe.com/docs/api/refunds/object#refund_object-transfer_reversal
     *
     */
    transfer_reversal?: {
      id: string;
      metadata: {
        userNoticeId?: string;
        type?: string;
        newspaperOrderId?: string;
        adType?: string;
      };
      transfer?: string;
    };
    amount?: number;
    receipt_url?: string;
  };
  id: string;

  /**
   * Gross amount of the transaction, in cents.
   */
  amount: number;

  /**
   * Net amount of the transaction, in cents.
   */
  net: number;

  created: number;
  currency: string;
  description: string;
  status: 'available' | 'pending' | 'succeeded';

  /**
   * See: https://stripe.com/docs/reports/reporting-categories
   */
  reporting_category?: 'charge' | 'refund' | 'transfer' | unknown;
  type: string;
};

/**
 * See: https://stripe.com/docs/api/customer_balance_transactions/object
 */
export type EStripeCustomerBalanceTransaction = {
  /**
   * The amount of the transaction (in cents). A negative value is a credit for the customer’s balance,
   * and a positive value is a debit to the customer’s balance.
   * See: The amount of the transaction. A negative value is a credit for the customer’s balance, and a positive value is a debit to the customer’s balance.
   */
  amount: number;

  /**
   * the ID of the customer the transaction belongs to
   * See: https://stripe.com/docs/api/customer_balance_transactions/object#customer_balance_transaction_object-customer
   */
  customer: string;

  /**
   * The ID of the invoice (if any) related to the transaction.
   * See: https://stripe.com/docs/api/customer_balance_transactions/object#customer_balance_transaction_object-customer
   */
  invoice: string;

  /**
   * The type of customer balance transaction
   * See: https://stripe.com/docs/api/customer_balance_transactions/object#customer_balance_transaction_object-customer
   */
  type?: string;
};

/**
 * See: https://stripe.com/docs/api/usage_records/object
 */
export type EStripeUsageRecord = {
  id: string;
  quantity: number;
  subscription_item: string;
  timestamp: number;
};

/**
 * See: https://stripe.com/docs/api/subscription_items/object
 */
export type EStripeSubscriptionItem = {
  id: string;
  price: {
    id: string;
  };
};

/**
 * See: https://stripe.com/docs/api/payouts/object
 */
export type EStripePayout = {
  id: string;
  status: 'paid' | 'pending' | 'in_transit' | 'canceled' | 'failed';
  arrival_date: number;
  metadata?: StripeNormalizedPayoutMetadata;
  amount: number;
  created: number;
  currency: string;
  automatic: boolean;
  description?: string;
  original_payout?: string;
};

/**
 * EPayoutData is not a core Stripe API type. Some parameters extended with EStripePayout
 * to fullfill the need of payout type that we are using in Payments page to show payouts data.
 */
export type EPayoutData = EStripePayout & {
  payments?: EStripeBalanceTransaction[];
  refunds?: EStripeBalanceTransaction[];
  charges?: EStripeBalanceTransaction[];
  feeRefunds?: EStripeBalanceTransaction[];
};

export type EStripePaymentMethodCard = {
  object: 'payment_method';
  type: 'card';

  id: string;
  customer: string;
  created: number;

  card: {
    brand: string;
    exp_month: number;
    exp_year: number;
    fingerprint: string;
    last4: string;
  };
};

export type EStripePaymentMethodUSBankAccount = {
  object: 'payment_method';
  type: 'us_bank_account';

  id: string;
  customer: string;
  created: number;

  us_bank_account: {
    account_type: 'checking' | 'savings';
    bank_name: string;
    fingerprint: string;
    last4: string;
    routing_number: string;
  };
};

const StripePaymentMethodUSBankAccountIdentitySchema = z.object({
  id: z.string(),
  object: z.literal('payment_method'),
  type: z.literal('us_bank_account')
});
export const StripePaymentMethodUSBankAccountSchema =
  z.custom<EStripePaymentMethodUSBankAccount>(
    data =>
      StripePaymentMethodUSBankAccountIdentitySchema.safeParse(data).success
  );

/**
 * See: https://stripe.com/docs/api/payment_methods/object
 */
export type EStripePaymentMethod =
  | EStripePaymentMethodCard
  | EStripePaymentMethodUSBankAccount;

/**
 * See: https://stripe.com/docs/api/transfers/object
 */
export type EStripeTransfer = {
  id: string;
  amount: number;
  created: number;
  metadata?: StripeTransferMetadata;
};

/**
 * See: https://stripe.com/docs/api/transfer_reversals/object
 */
export type EStripeTransferReversal = {
  id?: string;
  object?: 'transfer_reversal';
  amount?: number;
  balance_transaction?: string;
  // Timestamp in seconds
  created?: number;
  currency?: 'usd';
  destination_payment_refund?: string;
  metadata?: {
    type?: string;
    userNoticeId?: string;
    newspaperOrderId?: string;
    adType?: string;
  };
  source_refund?: string | null;
  transfer?: string;
};

export type EStripeSubscription = {
  id: string;
  metadata: Record<string, string | undefined>;
};

/**
 * See: https://docs.stripe.com/api/paper_checks/list
 * We manually create the type for this call because this method
 * is not implemented in the node api
 */
export type EStripePaperCheckListParams = {
  /**
   * Whether the check has been returned/bounced
   */
  bounced?: boolean;
  /**
   * Whether the check has been reconciled
   */
  reconciled?: boolean;
  /**
   * Only return paper checks for the customer specified by this customer ID.
   */
  customer?: string;
  /**
   * A cursor for use in pagination. ending_before is an object ID that
   * defines your place in the list. For instance, if you make a list request
   * and receive 100 objects, starting with obj_bar, your subsequent call can
   * include ending_before=obj_bar in order to fetch the previous page of the list.
   */
  ending_before?: string;
  /**
   * A limit on the number of objects to be returned.
   * Limit can range between 1 and 100, and the default is 10.
   */
  limit?: number;
  /**
   * A cursor for use in pagination. starting_after is an object ID that
   * defines your place in the list. For instance, if you make a list request
   * and receive 100 objects, ending with obj_foo, your subsequent call can
   * include starting_after=obj_foo in order to fetch the next page of the list.
   */
  starting_after?: string;
};

export type EStripePaperCheckListResponse = {
  data: EStripePaperCheck[];
  has_more: boolean;
  url: string;
  object: string;
};

export enum EStripePaperCheckStatus {
  UNRECONCILED = 'unreconciled',
  RECONCILED = 'reconciled'
}
